import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { api, extractTokens } from '../api/client';
import { endpoints, type Endpoints } from '../api/endpoints';
import type { AuthResult, Role, TokenPair, User } from '../api/types';
import i18n, { normalizeLanguage } from '../i18n';

const RANK: Record<Role, number> = { user: 0, admin: 1, super_admin: 2 };

export function roleAtLeast(role: Role | undefined, minimum: Role): boolean {
  return role !== undefined && RANK[role] >= RANK[minimum];
}

export function applyTheme(theme: string | undefined) {
  document.documentElement.dataset.theme = theme === 'light' ? 'light' : 'dark';
}

interface AuthState {
  user: User | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  acceptTokens(result: TokenPair | AuthResult): Promise<void>;
  logout(): Promise<void>;
  reload(): Promise<void>;
  setUser(user: User): void;
  hasRole(minimum: Role): boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children, client = endpoints }: { children: ReactNode; client?: Endpoints }) {
  const [user, setUserState] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((next: User) => {
    setUserState(next);
    applyTheme(next.preferences?.theme);
    const lang = next.preferences?.language;
    // Язык профиля главнее сохранённого в браузере: i18next при старте сам пишет туда язык
    // браузера, а выбор пользователя в интерфейсе сохраняется в профиль.
    if (lang) void i18n.changeLanguage(normalizeLanguage(lang));
  }, []);

  const reload = useCallback(async () => {
    setUser(await client.users.me());
  }, [client, setUser]);

  const acceptTokens = useCallback(
    async (result: TokenPair | AuthResult) => {
      api.tokens.setTokens(extractTokens(result));
      const user = (result as AuthResult).user;
      if (user) setUser(user);
      else await reload();
    },
    [reload, setUser],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      await acceptTokens(await client.auth.login(email, password));
    },
    [client, acceptTokens],
  );

  const logout = useCallback(async () => {
    try {
      await client.auth.logout(api.tokens.getRefreshToken());
    } catch {
      /* выход выполняется и при недоступном сервере */
    }
    api.tokens.clear();
    setUserState(null);
  }, [client]);

  useEffect(() => {
    api.onAuthLost = () => setUserState(null);
    let cancelled = false;
    (async () => {
      try {
        if (api.tokens.getRefreshToken() && (await api.refresh()) && !cancelled) await reload();
      } catch {
        api.tokens.clear();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      login,
      acceptTokens,
      logout,
      reload,
      setUser,
      hasRole: (minimum) => roleAtLeast(user?.role, minimum),
    }),
    [user, loading, login, acceptTokens, logout, reload, setUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
